import { Span } from '@opentelemetry/api';

export const Domains = {
  External: 'troweprice.com',
  Internal: 'awstrp.net',
};
export const AllDomains = Object.values(Domains);
const LOCALHOST = 'localhost';
const WWW = 'www';

export type HostnameMatcher = (hostname: string) => boolean;
export type HostnameSource = () => string;
export type NavigatorSource = () => Navigator;
export type PerformanceSource = () => Performance;

function hostnameFromWindow() {
  return window.location.hostname;
}

function isPerformanceNavigationTiming(
  entry: PerformanceEntry,
): entry is PerformanceNavigationTiming {
  return entry.entryType === 'navigation';
}

function navigatorFromWindow() {
  return window.navigator;
}

function performanceFromWindow() {
  return window.performance;
}

// Common Predicates
// =================

export function endsWith(base: string): HostnameMatcher {
  return (hostname: string) => hostname.endsWith(base);
}

// Audience
// ========

export enum Audience {
  External = 'external',
  Internal = 'internal',
  Unknown = 'unknown',
}

export interface AudienceMapping {
  audience: Audience;
  matcher: HostnameMatcher;
}

export type AudienceMappings = AudienceMapping[];

export const defaultAudienceMappings: AudienceMappings = [
  {
    audience: Audience.External,
    matcher: endsWith(Domains.External),
  },
  {
    audience: Audience.Internal,
    matcher: endsWith(Domains.Internal),
  },
];

// Environment
// ===========

export enum Environment {
  Dev = 'dev',
  Prod = 'prod',
  Sandbox = 'sandbox',
  Sit = 'sit',
  Stage = 'stage',
  Unknown = 'unknown',
}

export interface EnvironmentMapping {
  environment: Environment;
  matcher: HostnameMatcher;
}

export type EnvironmentMappings = EnvironmentMapping[];

function domainEqualsEnv(domainParts: string[], envName: string) {
  const targetDomainPart = domainParts[domainParts.length - 3]; // third part from right
  return targetDomainPart === envName;
}

function threePartDomainEqualsEnv(
  domainParts: string[],
  envName: string,
  barePart?: string,
) {
  // this is a domain like www.troweprice.com or wwwdev.troweprice.com
  const targetDomainPart = domainParts[0];
  if (barePart && targetDomainPart === barePart) {
    // this would be something like www.troweprice.com or www.awstrp.net
    return true;
  }
  return WWW + envName === targetDomainPart;
}

export function envDomainPartEquals(
  domains: string[],
  envName: string,
  barePart?: string,
): HostnameMatcher {
  return (hostname) => {
    if (!domains.find((domain) => hostname.endsWith(domain))) {
      // can't rely on below parsing if not an expected domain
      return false;
    }
    const domainParts = hostname.split('.');
    if (domainParts.length < 4) {
      return threePartDomainEqualsEnv(domainParts, envName, barePart);
    }
    return domainEqualsEnv(domainParts, envName);
  };
}

export const defaultEnvironmentMappings: EnvironmentMappings = [
  {
    environment: Environment.Prod,
    matcher: envDomainPartEquals(AllDomains, 'prod', WWW),
  },
  {
    environment: Environment.Stage,
    matcher: envDomainPartEquals(AllDomains, 'stage'),
  },
  {
    environment: Environment.Sit,
    matcher: envDomainPartEquals(AllDomains, 'sit'),
  },
  {
    environment: Environment.Dev,
    matcher: envDomainPartEquals(AllDomains, 'dev'),
  },
  {
    environment: Environment.Sandbox,
    matcher: envDomainPartEquals(AllDomains, LOCALHOST, LOCALHOST),
  },
  {
    environment: Environment.Sandbox,
    matcher: (hostname) => hostname === LOCALHOST,
  },
];

// Platform
// ========

export enum Platform {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Unknown = 'unknown',
}

// SiteAttributes
// ==============

export interface SiteAttributesOptions {
  audienceMappings?: AudienceMappings;
  environmentMappings?: EnvironmentMappings;
  hostnameSource?: HostnameSource;
  navigatorSource?: NavigatorSource;
  performanceSource?: PerformanceSource;
}

export class SiteAttributes {
  private readonly audienceMappings: AudienceMappings;
  private readonly environmentMappings: EnvironmentMappings;
  private readonly hostnameSource: HostnameSource;
  private readonly navigatorSource: NavigatorSource;
  private readonly performanceSource: PerformanceSource;

  constructor(options: SiteAttributesOptions = {}) {
    this.audienceMappings = options.audienceMappings ?? defaultAudienceMappings;
    this.environmentMappings = options.environmentMappings ?? defaultEnvironmentMappings;
    this.hostnameSource = options.hostnameSource ?? hostnameFromWindow;
    this.navigatorSource = options.navigatorSource ?? navigatorFromWindow;
    this.performanceSource = options.performanceSource ?? performanceFromWindow;
  }

  addAttributes(span: Span): Span {
    span.setAttribute('trp.audience', this.audience());
    span.setAttribute('trp.environment', this.environment());
    span.setAttribute('trp.navigationType', this.navigationType());
    span.setAttribute('trp.platform', this.platform());
    // intentionally not using http.user_agent as this isn't part of an http interaction
    span.setAttribute('trp.userAgent', this.userAgent());
    return span;
  }

  audience(): Audience {
    for (const m of this.audienceMappings) {
      if (m.matcher(this.hostnameSource())) {
        return m.audience;
      }
    }
    return Audience.Unknown;
  }

  environment(): Environment {
    for (const m of this.environmentMappings) {
      if (m.matcher(this.hostnameSource())) {
        return m.environment;
      }
    }
    return Environment.Unknown;
  }

  navigationType(): string {
    const navigationType = this.performanceSource()
      .getEntriesByType('navigation')
      .filter(isPerformanceNavigationTiming)
      .map((nav) => nav.type)[0];
    if (!navigationType) {
      return 'unknown';
    }
    return navigationType;
  }

  platform(): Platform {
    const navigator = this.navigatorSource();
    // fairly new feature
    // see: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData
    const userAgentData = (navigator as any).userAgentData;

    if (!userAgentData) {
      return Platform.Unknown;
    }
    if (userAgentData.mobile) {
      return Platform.Mobile;
    }
    return Platform.Desktop;
  }

  userAgent(): string {
    const navigator = this.navigatorSource();
    return navigator.userAgent;
  }
}
